// Reusable Connections presentation: metadata plus an optional module-only
// project Updates destination, supplied by the loaded record's parent.
// Interpretation lives in projectConnectionsModel.js; this file is presentation.
// The cluster is linked-only; the default module also preserves name-only metadata.
import { useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_LocationContext } from "react-router-dom";
import { Folder, Copy, Check } from "lucide-react";
import { T } from "../../../app/tokens.js";
import { NavRailIcon } from "../../../components/NavRailIcon.jsx";
import { interpretConnections } from "./projectConnectionsModel.js";
import { InternalLink } from "../../../components/InternalLink.jsx";
import { recordSwapState } from "../../../services/recordReturn.js";
import { getLeaseById } from "../../../data/leasesClient.js";
import { useSensitivityPolicy } from "../../../contexts/SensitivityPolicyContext.jsx";
import "./ProjectConnections.css";

const CLUSTER_ICONS = { location: "places", lease: "leases", slack: "slack", drive: "drive" };

function ConnectionChips({ entries, navigationState }) {
  const linked = entries.filter(entry => entry.href && entry.kind !== "lease");
  if (!linked.length) return null;
  return (
    <div data-testid="project-connections-compact" className="project-connections-compact"
      role="group" aria-label="Current project connections"
      style={{ "--connection-accent": T.blue }}>
      {linked.map(entry => {
        const label = `Open ${entry.title}: ${entry.label}${entry.external ? " (opens in a new tab)" : ""}`;
        const Anchor = entry.external ? "a" : InternalLink;
        return <Anchor key={entry.kind} href={entry.href} state={entry.external ? undefined : navigationState} className="project-connection-chip"
          data-testid={`connection-compact-${entry.kind}`} aria-label={label} title={label}
          style={{ color: entry.kind === "slack" ? T.purpleDeep : T.projectsHubNavy,
            background: entry.kind === "slack" ? T.purpleBg : T.hoverBg,
            borderColor: entry.kind === "slack" ? T.marketConsolidateBg : T.border,
            fontSize: T.fsXs, fontWeight: T.fwSemibold }}
          target={entry.external ? "_blank" : undefined} rel={entry.external ? "noopener noreferrer" : undefined}
          onClick={event => event.stopPropagation()}>
          <span aria-hidden="true" style={{ display: "inline-flex", flexShrink: 0, color: entry.kind === "location" ? T.green : entry.kind === "slack" ? T.purpleDeep : T.blue }}>
            <NavRailIcon itemKey={CLUSTER_ICONS[entry.kind]} size={13} />
          </span>
          <span>{entry.label}</span>
        </Anchor>;
      })}
    </div>
  );
}

function ConnectionCluster({ entries, navigationState }) {
  const linked = entries.filter(entry => entry.href);
  if (!linked.length) return null;
  return (
    <span data-testid="project-connections-cluster" className="project-connections-cluster"
      style={{ "--connection-muted": T.mid, "--connection-accent": T.blue, "--connection-border": T.border, "--connection-bg": T.card }}>
      {linked.map(entry => {
        const label = `Open ${entry.title}: ${entry.label}${entry.external ? " (opens in a new tab)" : ""}`;
        const Anchor = entry.external ? "a" : InternalLink;
        return (
          <Anchor key={entry.kind} href={entry.href} state={entry.external ? undefined : navigationState} className="project-connection-link"
            data-testid={`connection-cluster-${entry.kind}`} aria-label={label} title={label}
            target={entry.external ? "_blank" : undefined} rel={entry.external ? "noopener noreferrer" : undefined}
            onClick={event => event.stopPropagation()}>
            <span aria-hidden="true"><NavRailIcon itemKey={CLUSTER_ICONS[entry.kind]} size={14} /></span>
          </Anchor>
        );
      })}
    </span>
  );
}

function EntryIcon({ kind }) {
  const box = { width: 28, height: 28, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: T.blueSoft, color: T.projectsHubNavy };
  return (
    <span aria-hidden="true" data-testid={`connection-icon-${kind}`} style={box}>
      {kind === "location" && <NavRailIcon itemKey="places" size={16} />}
      {kind === "lease" && <NavRailIcon itemKey="leases" size={16} />}
      {kind === "slack" && <NavRailIcon itemKey="slack" size={16} />}
      {kind === "drive" && <Folder size={16} strokeWidth={1.9} />}
      {kind === "updates" && <NavRailIcon itemKey="feed" size={16} />}
    </span>
  );
}

function CodeCopy({ code }) {
  const [state, setState] = useState("idle"); // idle | copied | failed
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    clearTimeout(timer.current);
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(code);
      setState("copied");
    } catch { setState("failed"); }
    timer.current = setTimeout(() => setState("idle"), 2400);
  };
  return (
    <div data-testid="connections-code" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: T.hoverBg, border: `1px solid ${T.border}`, borderRadius: 10 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.mid }}>Project code</div>
        <div data-testid="connections-code-value" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: T.fsBase, fontWeight: T.fwBold, color: T.projectsHubNavy, overflowWrap: "anywhere" }}>{code}</div>
      </div>
      <button type="button" data-testid="connections-code-copy" onClick={copy} aria-label={`Copy project code ${code}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, border: `1px solid ${T.border}`, background: T.card, borderRadius: 7, padding: "5px 10px", fontSize: T.fsXs, fontWeight: T.fwBold, color: state === "failed" ? T.red : T.projectsHubNavy, cursor: "pointer" }}>
        {state === "copied" ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy"}
      </button>
      <span role="status" aria-live="polite" data-testid="connections-code-status" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        {state === "copied" ? "Project code copied" : state === "failed" ? "Could not copy the project code" : ""}
      </span>
    </div>
  );
}

function EntryRow({ entry, navigationState }) {
  const linkStyle = { display: "inline-flex", alignItems: "center", gap: 3, fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue, textDecoration: "none", whiteSpace: "nowrap" };
  return (
    <li data-testid={`connection-row-${entry.kind}`} style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 2px", borderTop: `1px solid ${T.border}` }}>
      <EntryIcon kind={entry.kind} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid }}>{entry.title}</div>
        <div data-testid={`connection-label-${entry.kind}`} style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, overflowWrap: "anywhere" }}>{entry.label}</div>
        {entry.detail && <div data-testid={entry.kind === "updates" ? "connection-updates-participation" : undefined}
          data-complete={entry.kind === "updates" ? String(Boolean(entry.note?.complete)) : undefined}
          style={{ fontSize: T.fsXs, color: entry.note?.available && !entry.note.complete ? T.ragAmberText : T.mid, overflowWrap: "anywhere",
            fontStyle: entry.note && !entry.note.available ? "italic" : "normal" }}>{entry.detail}</div>}
      </div>
      {entry.href && (entry.external ? (
        <a href={entry.href} target="_blank" rel="noopener noreferrer" data-testid={`connection-open-${entry.kind}`} aria-label={`Open ${entry.title}: ${entry.label} (opens in a new tab)`} style={linkStyle}>
          Open <span aria-hidden="true">↗</span>
        </a>
      ) : (
        <InternalLink href={entry.href} state={navigationState} data-testid={`connection-open-${entry.kind}`} aria-label={entry.kind === "updates" ? "Open Updates" : `Open ${entry.title}: ${entry.label}`} style={linkStyle}>
          Open <span aria-hidden="true">&rarr;</span>
        </InternalLink>
      ))}
    </li>
  );
}

export function ProjectConnections({ connections, updatesHref, variant = "module", returnLabel, updatesNote = null }) {
  const routerLocation = useContext(UNSAFE_LocationContext)?.location;
  const sensitivity = useSensitivityPolicy();
  const leaseId = connections?.lease?.leaseId;
  const canReadLease = sensitivity.canSee("leases.page");
  const [leaseRead, setLeaseRead] = useState(null);
  useEffect(() => {
    if (variant !== "module" || !leaseId || !canReadLease) return;
    let active = true;
    Promise.resolve().then(() => getLeaseById(leaseId)).then(
      data => { if (active) setLeaseRead({ id: leaseId, data }); },
      error => { if (active) setLeaseRead({ id: leaseId, error }); },
    );
    return () => { active = false; };
  }, [leaseId, variant, canReadLease]);
  const currentRead = leaseRead?.id === leaseId ? leaseRead : null;
  const { code, entries } = interpretConnections(connections, currentRead?.data);
  const navigationState = routerLocation && returnLabel
    ? recordSwapState({ kind: "project", path: routerLocation.pathname + routerLocation.search, label: returnLabel })
    : undefined;
  if (variant === "cluster") return <ConnectionCluster entries={entries} navigationState={navigationState} />;
  if (variant === "compact") return <ConnectionChips entries={entries} navigationState={navigationState} />;
  if (!updatesHref && !code && entries.length === 0) return null;
  return (
    <section data-testid="project-connections" data-variant={variant} aria-labelledby="project-connections-title" style={{ position: "relative", marginTop: 18 }}>
      <h3 id="project-connections-title" style={{ margin: "0 0 8px", fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubNavy }}>Connections</h3>
      {code && <CodeCopy code={code} />}
      {(updatesHref || entries.length > 0) && (
        <ul style={{ listStyle: "none", margin: code ? "8px 0 0" : 0, padding: 0 }}>
          {updatesHref && <EntryRow entry={{ kind: "updates", title: "Updates", label: "Weekly updates", href: updatesHref, detail: updatesNote?.text, note: updatesNote }}
            navigationState={{ ...navigationState, returnPath: routerLocation ? routerLocation.pathname + routerLocation.search : undefined }} />}
          {entries.map((e) => <EntryRow key={e.kind} entry={e} navigationState={{ ...navigationState, returnPath: routerLocation ? routerLocation.pathname + routerLocation.search : undefined }} />)}
          {leaseId && canReadLease && currentRead?.error && <li role="status" style={{ fontSize: T.fsXs, color: T.mid }}>Lease details unavailable: {currentRead.error.message}</li>}
        </ul>
      )}
    </section>
  );
}

export default ProjectConnections;
